import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Input } from '@components/atoms/Input';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { TextareaInput } from '@components/atoms/TextareaInput';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { FormGroup } from '@components/molecules/form/FormGroup';
import {
  useMaterialCatalog,
  useStockLevels,
  useWarehouses,
  useReceiveStock,
} from '@hooks/api/warehouse';
import {
  MaterialCatalog,
  StockLevel,
  Warehouse,
  ReceiveStockInput,
} from '@models/warehouse.model';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

interface ReceiveStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialCatalogId?: string | null;
  onSuccess?: () => void;
}

export const ReceiveStockModal: React.FC<ReceiveStockModalProps> = ({
  isOpen,
  onClose,
  initialCatalogId,
  onSuccess,
}) => {
  const tWarehouse = useTranslation('warehouse');
  const tCommon = useTranslation('common');

  // Form state
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialCatalog | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [quantity, setQuantity] = useState<string>('');
  const [unitCost, setUnitCost] = useState<number | null>(null);
  const [supplier, setSupplier] = useState<string>('');
  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // API Hooks
  const { data: materials = [] } = useMaterialCatalog();
  const { data: stockLevels = [] } = useStockLevels();
  const { data: warehouses = [] } = useWarehouses();
  const receiveStockMutation = useReceiveStock();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedMaterial(null);
      setSelectedWarehouse(null);
      setQuantity('');
      setUnitCost(null);
      setSupplier('');
      setReferenceNumber('');
      setNotes('');
    }
  }, [isOpen]);

  // Set default warehouse
  useEffect(() => {
    if (isOpen && warehouses.length > 0 && !selectedWarehouse) {
      const defaultWarehouse = warehouses.find((w) => w.isDefault) || warehouses[0];
      setSelectedWarehouse(defaultWarehouse);
    }
  }, [warehouses, selectedWarehouse, isOpen]);

  // Set initial material from prop
  useEffect(() => {
    if (isOpen && initialCatalogId && materials.length > 0 && !selectedMaterial) {
      const material = materials.find((m) => m.catalogId === initialCatalogId);
      if (material) {
        setSelectedMaterial(material);
        setSupplier(material.defaultSupplier || '');
        setUnitCost(material.averageCost || null);
      }
    }
  }, [initialCatalogId, materials, selectedMaterial, isOpen]);

  // Build options for ReactSelect
  const materialOptions: SelectOption[] = useMemo(
    () =>
      materials.map((m) => ({
        value: m.catalogId,
        label: `${m.name}${m.sku ? ` (${m.sku})` : ''}`,
      })),
    [materials],
  );

  const warehouseOptions: SelectOption[] = useMemo(
    () =>
      warehouses.map((w) => ({
        value: w.warehouseId,
        label: `${w.name}${w.isDefault ? ' (Default)' : ''}`,
      })),
    [warehouses],
  );

  // Get current stock level for selected material and warehouse
  const currentStockLevel = useMemo<StockLevel | undefined>(() => {
    if (!selectedMaterial || !selectedWarehouse) return undefined;
    return stockLevels.find(
      (sl) =>
        sl.catalogId === selectedMaterial.catalogId &&
        sl.warehouseId === selectedWarehouse.warehouseId
    );
  }, [stockLevels, selectedMaterial, selectedWarehouse]);

  // Calculate summary values
  const summary = useMemo(() => {
    const qty = parseFloat(quantity) || 0;
    const cost = unitCost || 0;
    const currentQty = currentStockLevel?.quantityOnHand || 0;
    const currentAvgCost = currentStockLevel?.averageCost || selectedMaterial?.averageCost || 0;

    const receiptTotal = qty * cost;
    const newQuantity = currentQty + qty;

    // Weighted average cost calculation
    let newAvgCost = currentAvgCost;
    if (newQuantity > 0) {
      newAvgCost = ((currentQty * currentAvgCost) + (qty * cost)) / newQuantity;
    }

    const costDifference = newAvgCost - currentAvgCost;

    // Sprint 30 change request (story-152 QA re-review, Bug #2). Was hardcoded English
    // "units"/"unit" inside a template literal with interpolation (guard blind spot #2) —
    // now threads the material's own unit through, falling back to the shared generic-unit
    // pair (Bug #3) rather than the bare English word.
    const unitWordPlural = selectedMaterial?.unit || tWarehouse.genericUnitPlural;
    const unitWordSingular = selectedMaterial?.unit || tWarehouse.genericUnitSingular;

    return {
      receiptTotal,
      currentQuantity: currentQty,
      newQuantity,
      currentAvgCost,
      newAvgCost,
      costDifference,
      calculationFormula: `(${currentQty} ${unitWordPlural} x $${currentAvgCost.toFixed(2)}) + (${qty} ${unitWordPlural} x $${cost.toFixed(2)}) = $${((currentQty * currentAvgCost) + (qty * cost)).toFixed(2)} / ${newQuantity} ${unitWordPlural} = $${newAvgCost.toFixed(2)}/${unitWordSingular}`,
    };
  }, [quantity, unitCost, currentStockLevel, selectedMaterial, tWarehouse]);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  // Handlers
  const handleSelectMaterial = useCallback((material: MaterialCatalog) => {
    setSelectedMaterial(material);
    setSupplier(material.defaultSupplier || '');
    setUnitCost(material.averageCost || null);
  }, []);

  const handleClearMaterial = useCallback(() => {
    setSelectedMaterial(null);
    setSupplier('');
    setUnitCost(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedMaterial || !selectedWarehouse || !quantity || !unitCost) {
      return;
    }

    const input: ReceiveStockInput = {
      catalogId: selectedMaterial.catalogId,
      warehouseId: selectedWarehouse.warehouseId,
      quantity: parseFloat(quantity),
      unitCost: unitCost || 0,
      supplier: supplier || undefined,
      referenceNumber: referenceNumber || undefined,
      notes: notes || undefined,
    };

    try {
      await receiveStockMutation.mutateAsync(input);
      toast.success(tWarehouse.receive.receivedSuccess);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error receiving stock:', error);
      toast.error(tWarehouse.receive.receiveFailed);
    }
  }, [
    selectedMaterial,
    selectedWarehouse,
    quantity,
    unitCost,
    supplier,
    referenceNumber,
    notes,
    receiveStockMutation,
    onSuccess,
    onClose,
    tWarehouse,
  ]);

  const isFormValid = selectedMaterial && selectedWarehouse && quantity && unitCost;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={tWarehouse.receiveStock}
      description={tWarehouse.receive.description}
      size="large"
      handleConfirm={handleSubmit}
      confirmText={
        receiveStockMutation.isPending ? tWarehouse.receive.receiving : tWarehouse.receiveStock
      }
      cancelText={tCommon.cancel}
      isLoading={receiveStockMutation.isPending}
      disableConfirm={!isFormValid || receiveStockMutation.isPending}
    >
      <div className="space-y-6">
        {/* Material Selection Section */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Inventory2Icon className="text-green-600" sx={{ fontSize: 18 }} />

          </h3>

          {!selectedMaterial ? (
            <FormGroup label={tWarehouse.selectMaterialRequired} htmlFor="materialSearch">
              <ReactSelect
                options={materialOptions}
                placeholder={tWarehouse.searchOrSelectMaterial}
                isSearchable
                isClearable
                onChange={(option) => {
                  if (option && 'value' in option) {
                    const material = materials.find((m) => m.catalogId === option.value);
                    if (material) handleSelectMaterial(material);
                  }
                }}
              />
            </FormGroup>
          ) : (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Inventory2Icon className="text-green-600 dark:text-green-400" sx={{ fontSize: 20 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{selectedMaterial.name}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedMaterial.sku || 'No SKU'}
                        {selectedMaterial.category && ` - ${selectedMaterial.category.name}`}
                      </p>
                    </div>
                    <button type="button"
                      onClick={handleClearMaterial}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{tWarehouse.currentStock}</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {currentStockLevel?.quantityOnHand || 0} {selectedMaterial.unit || tWarehouse.genericUnitPlural}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{tWarehouse.receive.avgCost}</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(currentStockLevel?.averageCost || selectedMaterial.averageCost || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">{tWarehouse.receive.supplier}</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {selectedMaterial.defaultSupplier || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Warehouse Selection */}
          {warehouses.length > 1 && (
            <div className="mt-3">
              <FormGroup label={tWarehouse.warehouseRequired} htmlFor="warehouseSelect">
                <ReactSelect
                  options={warehouseOptions}
                  placeholder={tWarehouse.selectWarehousePlaceholder}
                  isSearchable={false}
                  isClearable={false}
                  value={selectedWarehouse ? { value: selectedWarehouse.warehouseId, label: `${selectedWarehouse.name}${selectedWarehouse.isDefault ? ' (Default)' : ''}` } : null}
                  onChange={(option) => {
                    if (option && 'value' in option) {
                      const wh = warehouses.find((w) => w.warehouseId === option.value);
                      setSelectedWarehouse(wh || null);
                    }
                  }}
                />
              </FormGroup>
            </div>
          )}
        </div>

        {/* Receipt Details Section */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <ReceiptLongIcon className="text-green-600" sx={{ fontSize: 18 }} />

          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Quantity */}
            <FormGroup label={tWarehouse.quantityRequired} htmlFor="quantity">
              <Input
                id="quantity"
                type="number"
                min="0"
                step="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                suffix={selectedMaterial?.unit || tWarehouse.genericUnitPlural}
              />
            </FormGroup>

            {/* Unit Cost */}
            <FormGroup label={tWarehouse.receive.unitCostRequired} htmlFor="unitCost">
              <InputCurrency
                id="unitCost"
                value={unitCost}
                onChange={(value) => setUnitCost(value)}
                placeholder="0.00"
              />
              {selectedMaterial?.averageCost && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {tWarehouse.receive.lastCost} {formatCurrency(selectedMaterial.averageCost)}/{selectedMaterial.unit || tWarehouse.genericUnitSingular}
                </p>
              )}
            </FormGroup>

            {/* Supplier */}
            <FormGroup label={tWarehouse.receive.supplier} htmlFor="supplier">
              <Input
                id="supplier"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                placeholder={tWarehouse.receive.enterSupplierPlaceholder}
              />
            </FormGroup>

            {/* Reference Number */}
            <FormGroup label={tWarehouse.receive.batchReference} htmlFor="referenceNumber">
              <Input
                id="referenceNumber"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder={tWarehouse.receive.exampleBatchRef}
              />
            </FormGroup>
          </div>

          {/* Notes */}
          <div className="mt-4">
            <FormGroup label={tWarehouse.receive.notes} htmlFor="notes">
              <TextareaInput
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={tWarehouse.receive.notesPlaceholder}
                rows={2}
                resize="none"
              />
            </FormGroup>
          </div>
        </div>

        {/* Summary Section */}
        {selectedMaterial && quantity && unitCost && (
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <SummarizeIcon className="text-green-600" sx={{ fontSize: 18 }} />
              {tWarehouse.receive.summary}
            </h3>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="space-y-2 text-sm">
                {/* Receipt Total */}
                <div className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">{tWarehouse.receiptTotal}</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {quantity} {selectedMaterial.unit || tWarehouse.genericUnitPlural} x {formatCurrency(unitCost || 0)} ={' '}
                    <span className="text-green-600 dark:text-green-400 font-semibold">{formatCurrency(summary.receiptTotal)}</span>
                  </span>
                </div>

                {/* Stock Changes */}
                <div className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">{tWarehouse.currentStock}</span>
                  <span className="text-gray-900 dark:text-white">
                    {summary.currentQuantity} {selectedMaterial.unit || tWarehouse.genericUnitPlural}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">{tWarehouse.afterReceipt}</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">
                    {summary.newQuantity} {selectedMaterial.unit || tWarehouse.genericUnitPlural} (+{quantity})
                  </span>
                </div>

                {/* Cost Changes */}
                <div className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700">
                  <span className="text-gray-600 dark:text-gray-400">{tWarehouse.currentAvgCost}</span>
                  <span className="text-gray-900 dark:text-white">
                    {formatCurrency(summary.currentAvgCost)}/{selectedMaterial.unit || tWarehouse.genericUnitSingular}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1.5">
                  <span className="text-gray-600 dark:text-gray-400">{tWarehouse.newAvgCost}</span>
                  <div className="text-right">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(summary.newAvgCost)}/{selectedMaterial.unit || tWarehouse.genericUnitSingular}
                    </span>
                    {summary.costDifference !== 0 && (
                      <p className={`text-xs flex items-center justify-end gap-1 ${
                        summary.costDifference < 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {summary.costDifference < 0 ? (
                          <TrendingDownIcon sx={{ fontSize: 12 }} />
                        ) : (
                          <TrendingUpIcon sx={{ fontSize: 12 }} />
                        )}
                        {summary.costDifference < 0 ? '-' : '+'}
                        {formatCurrency(Math.abs(summary.costDifference))} {tWarehouse.receive.fromCurrent}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Calculation Info */}
            <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-700">
              <div className="flex items-start gap-2">
                <InfoOutlinedIcon className="text-blue-500 dark:text-blue-400 mt-0.5" sx={{ fontSize: 14 }} />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <p className="font-medium">{tWarehouse.weightedAverageCost}</p>
                  <p className="mt-0.5">{summary.calculationFormula}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
